import { useForm } from "react-hook-form";
import { useMediaQuery } from "@mantine/hooks";
import { Drawer, Stack, Text } from "@mantine/core";
import { Button } from "@onengine/component-library";
import type { FormQuestion } from "@/components/features/FormBuilder/types/form.types";
import type { FormLogicContext } from "@/components/features/FormBuilder/data/contextTriggers";
import { evaluateGroupEntry } from "@/components/features/FormBuilder/utils/evaluateLogic";
import type { EvalResult } from "@/components/features/FormBuilder/utils/evaluateLogic";
import { buildGroupEntrySchema } from "@/components/features/FormBuilder/utils/answerValidation";
import { PreviewQuestion } from "@/components/features/FormBuilder/components/Preview/PreviewQuestion";
import { Modal } from "@/components/ui/Modal/Modal";
import styles from "./GroupEntryEditor.module.scss";

export interface GroupEntryEditorProps {
  group: FormQuestion;
  item: Record<string, unknown> | null;
  isMobile?: boolean;
  logicContext?: FormLogicContext;
  onCancel: () => void;
  onSave: (values: Record<string, unknown>) => void;
}

export function GroupEntryEditor({
  group,
  item,
  isMobile,
  logicContext,
  onCancel,
  onSave,
}: GroupEntryEditorProps) {
  const isEditing = !!item;
  const itemLabel = group.groupConfig?.itemLabel || "Entry";
  const isNarrowViewport = useMediaQuery("(max-width: 480px)");
  const useDrawer = isMobile || isNarrowViewport;

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<Record<string, unknown>>({
    defaultValues: item ?? {},
  });

  const values = watch();
  const { visibleFields, requiredIds } = evaluateGroupEntry(group, values, logicContext);

  const itemEvalResult: EvalResult = {
    hiddenQuestions: new Set(),
    shownQuestions: new Set(),
    requiredByLogic: requiredIds,
    hiddenSections: new Set(),
    hiddenPages: new Set(),
    jumpToPageId: null,
    context: logicContext,
  };

  const submit = handleSubmit((formValues) => {
    const schema = buildGroupEntrySchema(group, formValues, logicContext);
    const result = schema.safeParse(formValues);

    visibleFields.forEach((field) => clearErrors(field.id));

    if (result.success) {
      onSave(formValues);
      return;
    }

    for (const issue of result.error.issues) {
      setError(String(issue.path[0]), { type: "custom", message: issue.message });
    }
  });

  const title = isEditing ? `Edit ${itemLabel}` : `Add ${itemLabel}`;

  const fields = (
    <Stack gap="lg">
      {group.description && group.description !== " " && (
        <Text size="sm" className={styles.description}>
          {group.description}
        </Text>
      )}
      {visibleFields.map((field) => (
        <PreviewQuestion
          key={field.id}
          question={field}
          control={control}
          evalResult={itemEvalResult}
          error={errors[field.id]?.message as string | undefined}
          onSig={() => {}}
          clearErrors={clearErrors}
        />
      ))}
    </Stack>
  );

  if (useDrawer) {
    return (
      <Drawer
        opened
        onClose={onCancel}
        position="bottom"
        withCloseButton={false}
        padding={0}
        classNames={{ content: styles.drawerContent, body: styles.drawerBody }}
      >
        <form onSubmit={submit} className={styles.drawerBody}>
          <div className={styles.drawerHeader}>
            <div className={styles.dragHandle} />
            <Text fw={600}>{title}</Text>
          </div>
          <div className={styles.drawerScroll}>{fields}</div>
          <div className={styles.drawerFooter}>
            <Button type="button" variant="neutral-secondary" size="base" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" variant="primary" size="base" onClick={submit}>
              Save
            </Button>
          </div>
        </form>
      </Drawer>
    );
  }

  return (
    <Modal isOpen onClose={onCancel} title={title} size="lg">
      <form onSubmit={submit}>
        {fields}
        <div className={styles.footer}>
          <Button type="button" variant="neutral-secondary" size="base" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" variant="primary" size="base" onClick={submit}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
